import type { PermissionGrant } from '@/lib/crm/components/properties/labels/PermissionsValueLabel';
import {
  getPropertyScoping,
  hasUnrecognizedScoping,
  parsePermissionGrantEntries,
} from '@/lib/permissions/grant-scoping';

import type {
  GrantSelection,
  RpcPermissionGrant,
} from './api-access-grant-builders';
import { PRIVATE_TARGET_ENTITY_TYPE } from './api-access-scopings';

/** An entry the GrantSelection model cannot represent: property-scoped or unrecognized scoping. */
function isPassthroughGrant(grant: PermissionGrant): boolean {
  const raw = grant as unknown as Record<string, unknown>;
  return getPropertyScoping(raw) !== null || hasUnrecognizedScoping(raw);
}

/**
 * Returns the grant entries that the GrantSelection model cannot represent
 * (property-scoped or unrecognized-scoping entries), so the edit slideout can
 * preserve them verbatim across a round-trip.
 */
export function extractPassthroughGrants(
  rawValue: unknown
): RpcPermissionGrant[] {
  return (
    parsePermissionGrantEntries(rawValue) as unknown as PermissionGrant[]
  ).filter(isPassthroughGrant) as unknown as RpcPermissionGrant[];
}

export interface StoredGrantSummary {
  /** Distinct entity types the credential can read. */
  readTypeCount: number;
  /** Distinct entity types the credential can also write. */
  writeTypeCount: number;
}

/**
 * Counts for the list row's one-line scope summary. Deliberately does not need
 * an association-target map: the public/private split is irrelevant to "how
 * many types", and the row must render before association types have loaded.
 */
/**
 * One-line scope summary from the stored grants ("Reads 10 types · writes 2",
 * "Reads 3 types · read-only", "No data access"). Shared by the settings row
 * and the credential record sub-header so both say the same thing.
 */
export function describeScope(rawPermissions: unknown): string {
  const { readTypeCount, writeTypeCount } =
    summarizeStoredGrants(rawPermissions);
  if (readTypeCount === 0) {
    return 'No data access';
  }
  const reads = `Reads ${readTypeCount} ${readTypeCount === 1 ? 'type' : 'types'}`;
  if (writeTypeCount > 0) {
    return `${reads} · writes ${writeTypeCount}`;
  }
  return `${reads} · read-only`;
}

export function summarizeStoredGrants(rawValue: unknown): StoredGrantSummary {
  const readTypes = new Set<string>();
  const writeTypes = new Set<string>();
  for (const grant of parsePermissionGrantEntries(
    rawValue
  ) as unknown as PermissionGrant[]) {
    if (grant.event_type_name) {
      writeTypes.add(grant.entity_type_name);
    } else {
      readTypes.add(grant.entity_type_name);
    }
  }
  return { readTypeCount: readTypes.size, writeTypeCount: writeTypes.size };
}

/**
 * Parse a stored `ahoy_permissions` value back into `GrantSelection[]` for the
 * edit flow — the reverse of `buildCredentialGrants`. Self-parsing: `rawValue`
 * may already be an array or still the raw JSON string form, matching how the
 * property value arrives from different callers.
 *
 * Grouping rule per entity type:
 * - included as a selection when it has at least one READ entry (empty/absent
 *   `event_type_name`) — a write-only group (shouldn't occur, since writes
 *   always accompany a read) is dropped rather than shown ownerless.
 * - `includePrivate` when any of its entries scopes to `ahoy_user`, classified
 *   by the scoping association's declared TARGET, never by name.
 * - `includeWrite` when any of its entries has a non-empty `event_type_name`.
 */
export function parseStoredGrants(
  rawValue: unknown,
  associationTargetTypes: ReadonlyMap<string, string>
): GrantSelection[] {
  const grants = (
    parsePermissionGrantEntries(rawValue) as unknown as PermissionGrant[]
  ).filter(grant => !isPassthroughGrant(grant));

  const entriesByType = new Map<string, PermissionGrant[]>();
  for (const grant of grants) {
    const entries = entriesByType.get(grant.entity_type_name) ?? [];
    entries.push(grant);
    entriesByType.set(grant.entity_type_name, entries);
  }

  const selections: GrantSelection[] = [];
  for (const [entityTypeName, entries] of entriesByType) {
    const hasRead = entries.some(entry => !entry.event_type_name);
    if (!hasRead) {
      continue;
    }
    // Derive read privacy from READ entries only. `includePrivate` drives the READ
    // grant on resave (buildPermissionGrants), so a private WRITE entry alongside a
    // public read must NOT flip it on — that would widen the read to the creator's
    // private records on round-trip (a privilege escalation).
    const includePrivate = entries.some(
      entry =>
        !entry.event_type_name &&
        !!entry.scoping_association_type_name &&
        associationTargetTypes.get(entry.scoping_association_type_name) ===
          PRIVATE_TARGET_ENTITY_TYPE
    );
    const includeWrite = entries.some(entry => !!entry.event_type_name);
    selections.push({ entityTypeName, includePrivate, includeWrite });
  }
  return selections;
}
